import { ExpandMoreOutlined, SendOutlined } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import {
  ChannelEndpoint,
  DeliveryAttempt,
  DeliveryStatus,
  QuotationExchangeChannel,
  QuotationInboxDto,
  QuotationOutboxDto,
  RequestForQuotationInboxDto,
  RequestForQuotationOutboxDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { readableLabel } from './quotationExchange.utils'

type ExchangeRecord =
  | RequestForQuotationInboxDto
  | RequestForQuotationOutboxDto
  | QuotationInboxDto
  | QuotationOutboxDto

const dateText = (value?: Date) =>
  value ? new Date(value).toLocaleString() : 'Not available'

const endpointText = (endpoint?: ChannelEndpoint) => {
  if (endpoint?.displayName && endpoint.address) {
    return `${endpoint.displayName} (${endpoint.address})`
  }

  return endpoint?.displayName || endpoint?.address || 'Addressless endpoint'
}

const deliveryColor = (status?: DeliveryStatus) => {
  if (status === DeliveryStatus.Failed) return 'error' as const
  if (
    status === DeliveryStatus.Delivered ||
    status === DeliveryStatus.ReadyForDownload
  )
    return 'success' as const
  if (status === DeliveryStatus.Sending || status === DeliveryStatus.Preparing)
    return 'warning' as const
  return 'default' as const
}

export function ChannelBadge({
  channel,
}: {
  channel?: QuotationExchangeChannel
}) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={
        channel === QuotationExchangeChannel.Scsn
          ? 'SCSN'
          : readableLabel(channel)
      }
      color={
        channel === QuotationExchangeChannel.Manual ? 'secondary' : 'primary'
      }
    />
  )
}

function DeliveryHistory({ attempts }: { attempts?: DeliveryAttempt[] }) {
  if (!attempts?.length) {
    return (
      <Typography color="text.secondary">No delivery attempts yet.</Typography>
    )
  }

  const orderedAttempts = [...attempts].sort((left, right) => {
    const leftDate = left.dateCreated
      ? new Date(left.dateCreated).getTime()
      : Number.NEGATIVE_INFINITY
    const rightDate = right.dateCreated
      ? new Date(right.dateCreated).getTime()
      : Number.NEGATIVE_INFINITY

    return rightDate - leftDate
  })

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {orderedAttempts.map((attempt, index) => (
        <Paper key={attempt.id || index} variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <ChannelBadge channel={attempt.channel} />
                <Chip
                  size="small"
                  color={deliveryColor(attempt.status)}
                  label={readableLabel(attempt.status)}
                />
              </Stack>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {endpointText(attempt.sender)} to{' '}
                {endpointText(attempt.recipient)}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Attempt ID: {attempt.id || 'Not available'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Provider ID: {attempt.externalAttemptId || 'Not available'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: { sm: 220 } }}>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Created: {dateText(attempt.dateCreated)}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Sent: {dateText(attempt.sentDate)}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Delivered: {dateText(attempt.deliveredDate)}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Document: {attempt.document ? 'Available' : 'Not available'}
              </Typography>
            </Box>
          </Stack>
          {attempt.failureMessage && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {attempt.failureMessage}
            </Alert>
          )}
        </Paper>
      ))}
    </Stack>
  )
}

export function ExchangeDeliveryDetails({
  record,
  onRetry,
  retrying = false,
  retryDisabled = false,
}: {
  record: ExchangeRecord
  onRetry?: () => void
  retrying?: boolean
  retryDisabled?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const receivedChannel =
    'receivedChannel' in record ? record.receivedChannel : undefined
  const selectedChannel =
    'selectedChannel' in record ? record.selectedChannel : undefined
  const responseChannel =
    'responseChannel' in record ? record.responseChannel : undefined
  const responseSender =
    'responseSender' in record ? record.responseSender : undefined
  const responseRecipient =
    'responseRecipient' in record ? record.responseRecipient : undefined
  const deliveryStatus =
    'deliveryStatus' in record ? record.deliveryStatus : undefined
  const deliveryFailure =
    'deliveryFailure' in record ? record.deliveryFailure : undefined
  const history =
    'deliveryHistory' in record ? record.deliveryHistory : undefined
  const documentAvailable =
    'documentDownloadAvailable' in record
      ? record.documentDownloadAvailable
      : false
  const responseDocumentAvailable =
    'responseDocumentDownloadAvailable' in record
      ? record.responseDocumentDownloadAvailable
      : false

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Exchange and delivery
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flex: 1,
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {receivedChannel && <ChannelBadge channel={receivedChannel} />}
          {selectedChannel && <ChannelBadge channel={selectedChannel} />}
          {responseChannel && <ChannelBadge channel={responseChannel} />}
          {deliveryStatus && (
            <Chip
              size="small"
              color={deliveryColor(deliveryStatus)}
              label={`Delivery: ${readableLabel(deliveryStatus)}`}
            />
          )}
        </Stack>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 1,
          mt: 1,
        }}
      >
        <Typography variant="caption">
          Sender: {endpointText(responseSender || record.sender)}
        </Typography>
        <Typography variant="caption">
          Recipient: {endpointText(responseRecipient || record.recipient)}
        </Typography>
        <Typography variant="caption">
          Document: {documentAvailable ? 'Available' : 'Not available'}
        </Typography>
        <Typography variant="caption">
          Response document:{' '}
          {responseDocumentAvailable ? 'Available' : 'Not available'}
        </Typography>
        <Typography variant="caption">
          Created: {dateText(record.dateCreated)}
        </Typography>
        <Typography variant="caption">
          Changed: {dateText(record.dateChanged)}
        </Typography>
      </Box>
      {deliveryFailure && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {deliveryFailure}
        </Alert>
      )}
      <Button
        size="small"
        endIcon={<ExpandMoreOutlined />}
        onClick={() => setExpanded((value) => !value)}
        sx={{ mt: 0.5 }}
      >
        Delivery history ({history?.length || 0})
      </Button>
      <Collapse in={expanded} unmountOnExit>
        {onRetry && (
          <Button
            size="small"
            variant="contained"
            startIcon={
              retrying ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendOutlined />
              )
            }
            disabled={retryDisabled || retrying}
            onClick={onRetry}
            sx={{ mt: 1 }}
          >
            Retry sending
          </Button>
        )}
        <DeliveryHistory attempts={history} />
      </Collapse>
    </Paper>
  )
}
